import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import {  useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { RootState } from "../../../../../store/Index";
import {
  setSelectedBook,
  updateScannedComments,
  updateOnlineUploadedComments,
} from "../../../../../store/slice/MemoryBookSlice";
import {
  updateMemoryBookAssignment,
  roleIncludesOnline,
  roleIncludesPhysical,
} from "./service/memoryBook.api";
import { tokens } from "../../../../../theme";

interface MemoryBookUpdateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MemoryBookUpdateModal: React.FC<MemoryBookUpdateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
 const theme = useTheme();
 const colors = tokens(theme.palette.mode)
  const { selectedBook } = useSelector((state: RootState) => state.memoryBook);

  const [scannedInput, setScannedInput] = useState<string>("");
  const [uploadedInput, setUploadedInput] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scannedCount = parseInt(scannedInput, 10) || 0;
  const uploadedCount = parseInt(uploadedInput, 10) || 0;

  const showPhysical =
    roleIncludesPhysical(selectedBook?.role) &&
    (selectedBook?.physical_comments ?? 0) > 0;
  const showOnline =
    roleIncludesOnline(selectedBook?.role) &&
    (selectedBook?.online_comments ?? 0) > 0;

  const physicalError =
    showPhysical &&
    !!scannedInput &&
    (!selectedBook ||
      isNaN(scannedCount) ||
      scannedCount <= 0 ||
      scannedCount > selectedBook.unfinished_comments);

  const onlineError =
    showOnline &&
    !!uploadedInput &&
    (!selectedBook ||
      isNaN(uploadedCount) ||
      uploadedCount <= 0 ||
      uploadedCount > selectedBook.unfinished_online_comments);

  // Progress calculations for the preview bars
  const physicalDone = selectedBook
    ? selectedBook.physical_comments - selectedBook.unfinished_comments
    : 0;
  const onlineDone = selectedBook
    ? selectedBook.online_comments - selectedBook.unfinished_online_comments
    : 0;

  const physicalProgress = selectedBook?.physical_comments
    ? Math.min(
        ((physicalDone + scannedCount) / selectedBook.physical_comments) * 100,
        100,
      )
    : 0;

  const onlineProgress = selectedBook?.online_comments
    ? Math.min(
        ((onlineDone + uploadedCount) / selectedBook.online_comments) * 100,
        100,
      )
    : 0;

  const handleSubmit = async () => {
    if (isSubmitting || !selectedBook || !selectedBook.assignmentId) {
      if (!selectedBook?.assignmentId) setError("Assignment not found.");
      return;
    }

    if (scannedCount === 0 && uploadedCount === 0) {
      setError(
        "Please enter progress for physical scanning, online uploads, or both.",
      );
      return;
    }

    if (physicalError || onlineError) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const updatedPayload: {
        done_comments?: number;
        uploaded_comments?: number;
      } = {};

      if (scannedCount > 0) {
        updatedPayload.done_comments = physicalDone + scannedCount;
      }
      if (uploadedCount > 0) {
        updatedPayload.uploaded_comments = onlineDone + uploadedCount;
      }

      await updateMemoryBookAssignment(
        selectedBook.assignmentId,
        updatedPayload,
      );

      if (scannedCount > 0) {
        dispatch(updateScannedComments({ scanned: scannedCount }));
      }
      if (uploadedCount > 0) {
        dispatch(updateOnlineUploadedComments({ uploaded: uploadedCount }));
      }

      const finalUnfinishedPhysical =
        selectedBook.unfinished_comments - scannedCount;
      const finalUnfinishedOnline =
        selectedBook.unfinished_online_comments - uploadedCount;
      const isComplete =
        finalUnfinishedPhysical <= 0 && finalUnfinishedOnline <= 0;

      dispatch(
        setSelectedBook({
          ...selectedBook,
          unfinished_comments: Math.max(finalUnfinishedPhysical, 0),
          unfinished_online_comments: Math.max(finalUnfinishedOnline, 0),
          status: isComplete ? "completed" : "in_progress",
        }),
      );

      setScannedInput("");
      setUploadedInput("");

      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (err) {
      console.error("Failed to update tracking execution metrics:", err);
      setError("Failed to update progress. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleCancel = () => {
    if (isSubmitting) return;
    setScannedInput("");
    setUploadedInput("");
    setError(null);
    onClose();
  };

  

  return (
    <Dialog
      open={open}
      TransitionComponent={Fade}
      keepMounted
      onClose={handleCancel}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "520px",
          borderRadius: 3,
          p: 0,
          background: colors.primary[900],
          backdropFilter: "blur(20px)",
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Update Progress
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 3, color: colors.primary[500] }}
          >
            How many comments have you scanned or uploaded?
          </Typography>

          {/* Book Info Section with Live Progress Bars */}
          <Box
            sx={{
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              backgroundColor: colors.primary[900],
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontSize: "1.1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Memory Book:{" "}
              <strong>{selectedBook?.title || "Not selected"}</strong>
            </Typography>

            {/* Physical Bar */}
            {showPhysical && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Scanned Physical Comments Progress
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {physicalDone + scannedCount} /{" "}
                  {selectedBook?.physical_comments || 0} Remaining:{" "}
                  {Math.max(
                    (selectedBook?.unfinished_comments || 0) - scannedCount,
                    0,
                  )}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={physicalProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "rgba(0,0,0,0.05)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: colors.primary[400],
                  },
                }}
              />
            </Box>
            )}

            {/* Online Bar */}
            {showOnline && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Uploaded Online Comments Progress
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {onlineDone + uploadedCount} /{" "}
                  {selectedBook?.online_comments || 0} Remaining:{" "}
                  {Math.max(
                    (selectedBook?.unfinished_online_comments || 0) -
                      uploadedCount,
                    0,
                  )}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={onlineProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "rgba(0,0,0,0.05)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: colors.primary[500],
                  },
                }}
              />
            </Box>
            )}
          </Box>

          {/* Side-by-Side Dual Processing Form Inputs */}
          <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
            {showPhysical && (
            <TextField
              fullWidth
              type="number"
              label="Physical scanned"
              value={scannedInput}
              onChange={(e) => {
                setScannedInput(e.target.value);
                setError(null);
              }}
              placeholder="Qty"
              error={physicalError}
              helperText={
                physicalError && selectedBook
                  ? `Max: ${selectedBook.unfinished_comments}`
                  : ""
              }
              disabled={isSubmitting}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  "& fieldset": {
                    borderColor:
                      colors.primary[500],
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": { borderColor: colors.primary[400]},
                  "&.Mui-focused fieldset": { borderColor: colors.primary[400] },
                },
              }}
            />
            )}

            {showOnline && (
            <TextField
              fullWidth
              type="number"
              label="Online uploaded"
              value={uploadedInput}
              onChange={(e) => {
                setUploadedInput(e.target.value);
                setError(null);
              }}
              placeholder="Qty"
              error={onlineError}
              helperText={
                onlineError && selectedBook
                  ? `Max: ${selectedBook.unfinished_online_comments}`
                  : ""
              }
              disabled={isSubmitting}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  "& fieldset": {
                    borderColor:
                     colors.primary[500],
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": { borderColor: colors.primary[500]},
                  "&.Mui-focused fieldset": { borderColor: colors.primary[500] },
                },
              }}
            />
            )}
          </Box>

          {error && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mb: 2, fontWeight: 500 }}
            >
              {error}
            </Typography>
          )}

          {/* Action Row */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                borderColor: colors.primary[800],
                
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                physicalError ||
                onlineError ||
                (!scannedInput && !uploadedInput)
              }
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
              }}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </Box>
        </motion.div>
      </Box>
    </Dialog>
  );
};

export default MemoryBookUpdateModal;
